/**
 * Ticket Service
 *
 * Handles all API calls related to the support ticketing system:
 * - User-facing ticket operations
 * - Admin ticket management
 * - Ticket templates
 */

import api from './api';
import type {
  Ticket,
  TicketFilters,
  TicketResponse,
  TicketsResponse,
  CreateTicketData,
  UpdateTicketData,
  AddTicketMessageData,
  AddInternalNoteData,
  AssignTicketData,
  BulkUpdateTicketsData,
  TicketMessageResponse,
  TicketAttachmentResponse,
  TicketStatisticsResponse,
  TicketTemplate,
  TicketTemplatesResponse,
  TicketTemplateResponse,
  CreateTemplateData,
  UpdateTemplateData,
  BulkUpdateTicketsResponse,
  AvailableAdminsResponse,
} from '../types/ticket';

/**
 * Build query string from ticket filters
 */
function buildTicketQuery(filters?: TicketFilters): string {
  if (!filters) return '';

  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.category) params.append('category', filters.category);
  if (filters.priority) params.append('priority', filters.priority);
  if (filters.assigned_to !== undefined) {
    params.append('assigned_to', filters.assigned_to.toString());
  }
  if (filters.account_id) params.append('account_id', filters.account_id.toString());
  if (filters.search) params.append('search', filters.search);
  if (filters.sort_by) params.append('sort_by', filters.sort_by);
  if (filters.sort_order) params.append('sort_order', filters.sort_order);
  if (filters.per_page) params.append('per_page', filters.per_page.toString());
  if (filters.page) params.append('page', filters.page.toString());

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

// =============================================================================
// User-facing Ticket Operations
// =============================================================================

/**
 * Fetch all tickets for the current user
 */
export async function getTickets(filters?: TicketFilters): Promise<TicketsResponse> {
  const query = buildTicketQuery(filters);
  const response = await api.get<TicketsResponse>(`/api/tickets${query}`);
  return response.data;
}

/**
 * Get a single ticket by ID
 */
export async function getTicket(ticketId: number): Promise<Ticket> {
  const response = await api.get<Ticket>(`/api/tickets/${ticketId}`);
  return response.data;
}

/**
 * Create a new ticket
 */
export async function createTicket(data: CreateTicketData): Promise<TicketResponse> {
  const response = await api.post<TicketResponse>('/api/tickets', data);
  return response.data;
}

/**
 * Add a message to a ticket
 */
export async function addTicketMessage(
  ticketId: number,
  data: AddTicketMessageData
): Promise<TicketMessageResponse> {
  const response = await api.post<TicketMessageResponse>(
    `/api/tickets/${ticketId}/messages`,
    data
  );
  return response.data;
}

/**
 * Upload an attachment to a ticket
 */
export async function uploadTicketAttachment(
  ticketId: number,
  file: File,
  messageId?: number
): Promise<TicketAttachmentResponse> {
  const formData = new FormData();
  formData.append('file', file);
  if (messageId) {
    formData.append('message_id', messageId.toString());
  }

  const response = await api.post<TicketAttachmentResponse>(
    `/api/tickets/${ticketId}/attachments`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

/**
 * Close a ticket
 */
export async function closeTicket(ticketId: number): Promise<TicketResponse> {
  const response = await api.post<TicketResponse>(`/api/tickets/${ticketId}/close`);
  return response.data;
}

/**
 * Reopen a closed ticket
 */
export async function reopenTicket(ticketId: number): Promise<TicketResponse> {
  const response = await api.post<TicketResponse>(`/api/tickets/${ticketId}/reopen`);
  return response.data;
}

/**
 * Get ticket statistics for the current user
 */
export async function getTicketStatistics(): Promise<TicketStatisticsResponse> {
  const response = await api.get<TicketStatisticsResponse>('/api/tickets/statistics');
  return response.data;
}

/**
 * Download a ticket attachment
 */
export function getAttachmentDownloadUrl(attachmentId: number): string {
  return `/api/tickets/attachments/${attachmentId}/download`;
}

// =============================================================================
// Admin Ticket Operations
// =============================================================================

/**
 * Get all tickets (admin view)
 */
export async function getAdminTickets(filters?: TicketFilters): Promise<TicketsResponse> {
  const query = buildTicketQuery(filters);
  const response = await api.get<TicketsResponse>(`/api/admin/tickets${query}`);
  return response.data;
}

/**
 * Assign a ticket to an admin
 */
export async function assignTicket(
  ticketId: number,
  data: AssignTicketData
): Promise<TicketResponse> {
  const response = await api.post<TicketResponse>(
    `/api/admin/tickets/${ticketId}/assign`,
    data
  );
  return response.data;
}

/**
 * Update ticket details (admin only)
 */
export async function updateTicket(
  ticketId: number,
  data: UpdateTicketData
): Promise<TicketResponse> {
  const response = await api.put<TicketResponse>(`/api/admin/tickets/${ticketId}`, data);
  return response.data;
}

/**
 * Add an internal note to a ticket (admin only)
 */
export async function addInternalNote(
  ticketId: number,
  data: AddInternalNoteData
): Promise<TicketMessageResponse> {
  const response = await api.post<TicketMessageResponse>(
    `/api/admin/tickets/${ticketId}/internal-notes`,
    data
  );
  return response.data;
}

/**
 * Bulk update multiple tickets (admin only)
 */
export async function bulkUpdateTickets(
  data: BulkUpdateTicketsData
): Promise<BulkUpdateTicketsResponse> {
  const response = await api.post<BulkUpdateTicketsResponse>(
    '/api/admin/tickets/bulk-update',
    data
  );
  return response.data;
}

/**
 * Delete a ticket (admin only)
 */
export async function deleteTicket(ticketId: number): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(`/api/admin/tickets/${ticketId}`);
  return response.data;
}

/**
 * Get ticket statistics (admin)
 */
export async function getAdminTicketStatistics(): Promise<TicketStatisticsResponse> {
  const response = await api.get<TicketStatisticsResponse>('/api/admin/tickets/statistics');
  return response.data;
}

/**
 * Get list of available admins for assignment
 */
export async function getAvailableAdmins(): Promise<AvailableAdminsResponse[]> {
  const response = await api.get<AvailableAdminsResponse[]>(
    '/api/admin/tickets/available-admins'
  );
  return response.data;
}

// =============================================================================
// Ticket Template Operations (Admin)
// =============================================================================

/**
 * Get all ticket templates (admin)
 */
export async function getTicketTemplates(filters?: {
  category?: string;
  is_active?: boolean;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  per_page?: number;
  page?: number;
}): Promise<TicketTemplatesResponse> {
  const params = new URLSearchParams();

  if (filters?.category) params.append('category', filters.category);
  if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());
  if (filters?.search) params.append('search', filters.search);
  if (filters?.sort_by) params.append('sort_by', filters.sort_by);
  if (filters?.sort_order) params.append('sort_order', filters.sort_order);
  if (filters?.per_page) params.append('per_page', filters.per_page.toString());
  if (filters?.page) params.append('page', filters.page.toString());

  const queryString = params.toString();
  const url = queryString ? `/api/admin/ticket-templates?${queryString}` : '/api/admin/ticket-templates';

  const response = await api.get<TicketTemplatesResponse>(url);
  return response.data;
}

/**
 * Get active templates for public use
 */
export async function getActiveTemplates(category?: string): Promise<TicketTemplate[]> {
  const url = category
    ? `/api/admin/ticket-templates/active?category=${category}`
    : '/api/admin/ticket-templates/active';

  const response = await api.get<TicketTemplate[]>(url);
  return response.data;
}

/**
 * Get a single template by ID
 */
export async function getTicketTemplate(templateId: number): Promise<TicketTemplate> {
  const response = await api.get<TicketTemplate>(`/api/admin/ticket-templates/${templateId}`);
  return response.data;
}

/**
 * Create a new ticket template (admin)
 */
export async function createTicketTemplate(
  data: CreateTemplateData
): Promise<TicketTemplateResponse> {
  const response = await api.post<TicketTemplateResponse>('/api/admin/ticket-templates', data);
  return response.data;
}

/**
 * Update a ticket template (admin)
 */
export async function updateTicketTemplate(
  templateId: number,
  data: UpdateTemplateData
): Promise<TicketTemplateResponse> {
  const response = await api.put<TicketTemplateResponse>(
    `/api/admin/ticket-templates/${templateId}`,
    data
  );
  return response.data;
}

/**
 * Delete a ticket template (admin)
 */
export async function deleteTicketTemplate(templateId: number): Promise<{ message: string }> {
  const response = await api.delete<{ message: string }>(
    `/api/admin/ticket-templates/${templateId}`
  );
  return response.data;
}

/**
 * Toggle template active status (admin)
 */
export async function toggleTemplateActive(
  templateId: number
): Promise<TicketTemplateResponse> {
  const response = await api.post<TicketTemplateResponse>(
    `/api/admin/ticket-templates/${templateId}/toggle-active`
  );
  return response.data;
}
